import React from 'react';
import PropTypes from 'prop-types';

/**
 * UserTable - Displays list of portal users
 *
 * Shows user information in a table with action buttons
 *
 * @param {Object} props
 * @param {Array} props.users - List of users to display
 * @param {string} props.filterText - Filter text for search
 * @param {Function} props.onFilterChange - Filter change handler
 * @param {Function} props.onEdit - Edit user handler
 * @param {Function} props.onViewForms - View forms handler
 * @param {Function} props.onDelete - Delete user handler
 */
const UserTable = ({
  users,
  filterText,
  onFilterChange,
  onEdit,
  onViewForms,
  onDelete,
}) => {
  const getPermissionClass = (user) => {
    const authority = user.authorities?.[0]?.authority;
    if (authority === 'Admin') return 'admin';
    if (authority === 'Office') return 'office';
    if (authority === 'AS') return 'office';
    return 'dm';
  };

  const getPermissionLabel = (user) => {
    return user.authorities?.[0]?.authority || user.permission;
  };

  const formatSubAreas = (user) => {
    if (
      !Array.isArray(user.assignedSubAreas) ||
      user.assignedSubAreas.length === 0
    ) {
      return '—';
    }

    return user.assignedSubAreas
      .map((subArea) => {
        const districtName = subArea.district?.districtName;
        return districtName
          ? `${subArea.name} (${districtName})`
          : subArea.name;
      })
      .join(', ');
  };

  return (
    <div className="section-card">
      <div className="section-header">
        <h2>All Portal Users ({users.length})</h2>
        <input
          type="text"
          placeholder="Filter users..."
          value={filterText}
          onChange={(e) => onFilterChange(e.target.value)}
          className="filter-input"
        />
      </div>

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Username</th>
              <th>Name</th>
              <th>Email</th>
              <th>Permission</th>
              <th>Sub-Areas</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.length > 0 ? (
              users.map((user, index) => (
                <tr key={user.id || index}>
                  <td>
                    <span className="username-badge">{user.userName}</span>
                  </td>
                  <td>
                    {user.userFirst} {user.userLast}
                  </td>
                  <td>{user.email}</td>
                  <td>
                    <span
                      className={`permission-badge ${getPermissionClass(user)}`}
                    >
                      {getPermissionLabel(user)}
                    </span>
                  </td>
                  <td style={{ maxWidth: '320px' }}>{formatSubAreas(user)}</td>
                  <td>
                    {user.disabled ? (
                      <span className="permission-badge dm">Disabled</span>
                    ) : (
                      <span className="permission-badge office">Active</span>
                    )}
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn-edit"
                        onClick={() => onEdit(user.userName)}
                      >
                        Edit
                      </button>
                      <button
                        className="btn-view"
                        onClick={() => onViewForms(user.id)}
                      >
                        View Forms
                      </button>
                      <button
                        className="btn-delete"
                        onClick={() => onDelete(user.id, user.userName)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan="7"
                  style={{
                    textAlign: 'center',
                    padding: '20px',
                    color: '#718096',
                  }}
                >
                  No users found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

UserTable.propTypes = {
  users: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      userName: PropTypes.string,
      userFirst: PropTypes.string,
      userLast: PropTypes.string,
      email: PropTypes.string,
      authorities: PropTypes.array,
      permission: PropTypes.string,
      assignedSubAreas: PropTypes.array,
      disabled: PropTypes.bool,
    })
  ).isRequired,
  filterText: PropTypes.string,
  onFilterChange: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onViewForms: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

UserTable.defaultProps = {
  filterText: '',
};

export default UserTable;
